import { useEffect, useId, useMemo } from 'react';
import { cn } from './ui';
import { useT } from '@/core/i18n';
import { bootScreenIsUp, holdBootScreen } from '@/core/boot';
import { MARK_RATIO } from './brand';
import { MARK_VIEWBOX, markInnerWithIds } from './markInline';

/* =============================================================================
 * Loading
 * -----------------------------------------------------------------------------
 * Three states, deliberately different, because they mean different things:
 *
 *   BrandLoader   the app itself is starting — the mark assembles itself
 *   Spinner       one action is in flight inside a button or a row
 *   PageLoading   a screen's chunk is arriving
 *
 * A single spinner for all three tells the user nothing about how long to wait.
 * ========================================================================== */

/**
 * The mark, assembling itself.
 *
 * =============================================================================
 * THE TWO THINGS THIS HAS BEEN BEFORE
 * =============================================================================
 * First it drew its own logo: a circle, a polyline for the N and a lozenge for
 * a leaf, stroked on with dasharray so it appeared to draw itself. The motion
 * was good and the mark was not the mark — a seventh hand-plotted approximation,
 * and the one that survived the cleanup of the other six because its path data
 * was different enough to slip the guard.
 *
 * Then it was the real artwork as an <img> with a ring turning around it. Honest
 * and dull: a picture with a cursor beside it, which says nothing about this
 * application except that it is busy.
 *
 * =============================================================================
 * WHAT IT IS NOW
 * =============================================================================
 * The real artwork, inlined so its parts are addressable, animated in the order
 * the logo is built: the faceted planes drop into place across the mark, left to
 * right, and settle into the block. Then it holds, and begins again.
 *
 * Nothing here is a drawing. Every shape is the one in public/brand/mark.svg —
 * see markInline.ts for how it gets here and what that costs.
 *
 * The choreography is in app.css under `mark-` so the whole sequence can be
 * retimed in one place, and so `prefers-reduced-motion` can replace it with a
 * still mark and a quiet pulse.
 */
export function BrandLoader({
  message, size = 96,
}: { message?: string; size?: number }) {
  const t = useT();

  /* Every gradient, mask and reused path in the artwork is referenced by id.
     Inlined, those ids are shared with the rest of the document — so each
     instance gets its own set. See markInline.ts. */
  const instance = useId().replace(/:/g, '');
  const markup = useMemo(() => markInnerWithIds(`m${instance}-`), [instance]);

  return (
    <div className="flex flex-col items-center gap-5">
      {/* The chip is the same treatment as everywhere else: the artwork is dark
          green and this screen is bg-bg, which is nearly black on a dark
          scheme. See BrandMark. */}
      <span className="brand-chip-auto inline-grid place-items-center rounded-card"
            style={{ padding: Math.max(4, Math.round(size * 0.1)) }}>
        <svg
          viewBox={MARK_VIEWBOX}
          width={size}
          height={Math.round(size / MARK_RATIO)}
          className="mark-loader"
          role="img"
          aria-label={t('ld.loading')}
          dangerouslySetInnerHTML={{ __html: markup }}
        />
      </span>

      {message && (
        <p className="text-[12.5px] text-subtle animate-pulse">{message}</p>
      )}
    </div>
  );
}

/**
 * The whole-screen loading state, and the only one.
 *
 * There were four of these: two hand-built wrappers, this, and — in the
 * two-factor gate — a bare BrandLoader with no wrapper at all, which put the
 * mark at the top of the document instead of the middle of the screen.
 *
 * It also holds the boot overlay up. index.html has been animating this exact
 * logo since before the bundle arrived; a startup screen that draws its own copy
 * underneath, and then takes over when the overlay fades, is the same animation
 * restarted for no reason. So while the overlay is up this draws nothing and
 * simply keeps it there. See src/core/boot.ts.
 */
export function FullScreenLoader({ message }: { message?: string }) {
  const covered = bootScreenIsUp();

  useEffect(() => holdBootScreen(), []);

  /* Nothing to draw: the overlay is on top, opaque, and already showing this. */
  if (covered) return null;

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-bg">
      <BrandLoader {...(message ? { message } : {})} />
    </div>
  );
}

/** Inline, for buttons and table rows. */
export function Spinner({ size = 16, className }: { size?: number; className?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={cn('spinner', className)}
      aria-hidden="true"
    >
      <circle
        cx="12" cy="12" r="9"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeDasharray="44"
        strokeDashoffset="33"
        opacity="0.9"
      />
    </svg>
  );
}

/**
 * Shown while a lazily-loaded screen arrives. Deliberately shaped like the
 * page it is replacing — a header, some tiles, a table — so the layout does
 * not jump when the real thing lands.
 */
export function PageLoading() {
  return (
    <div className="flex flex-col gap-4 max-w-[1400px] mx-auto page-enter" aria-busy="true">
      <div className="flex flex-col gap-2">
        <div className="skeleton h-7 w-48 rounded-card" />
        <div className="skeleton h-4 w-72 rounded-card" />
      </div>

      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="skeleton h-[104px] rounded-card" />
        ))}
      </div>

      <div className="skeleton h-[320px] rounded-card" />
    </div>
  );
}
